/**
 * Datenmodell des Partner-Backends.
 *
 * Bubble hält die Daten relational (User → Fotoboxen/Extras/Drucker/Pakete →
 * Angebote) und rechnet daraus die flache data.json aus, die das Frontend
 * liest. Genau diese Trennung bilden wir hier nach:
 *
 *   data/partners.json   Quelldaten, so wie der Vermieter sie pflegt
 *   data/data.json       generiertes Ergebnis fürs Frontend (nicht von Hand
 *                        bearbeiten — wird bei jedem Speichern überschrieben)
 *
 * Die Struktur ist bewusst so geschnitten, dass jede Liste später eine
 * Supabase-Tabelle wird und jedes `id`-Feld ein Primärschlüssel.
 */

// Auswahlwerte — 1:1 aus dem Bubble-Backend übernommen.
export const BOOTH_TYPES = [
  "Standard",
  "Spiegel",
  "Fahrzeug",
  "Kabine",
  "360 Grad",
];

export const CAM_TYPES = [
  "Spiegelreflexkamera",
  "Digitale Systemkamera (DSLM)",
  "HD-Kamera",
  "4K-Kamera",
];

export const LIGHT_TYPES = [
  "Studio-Blitz",
  "Foto-Blitz",
  "LED-Blitz",
  "LED-Dauerlicht",
];

// Extras sind nach Kategorie gruppiert; die Kategorie steuert, welche Felder
// das Formular zeigt und in welche Paket-Liste das Extra einsortiert wird.
export const EXTRA_CATEGORIES = [
  { key: "Fotodruck", label: "Fotodruck", max: 1 },
  { key: "Requisiten", label: "Requisiten", max: null },
  { key: "Hintergrund", label: "Hintergrund", max: null },
  { key: "Stundenpaket", label: "Stundenpaket", max: 1 },
  { key: "Sonstiges", label: "Sonstiges", max: null },
];

export const UNLIMITED_PRINTS = 999;

/**
 * Vermittlungsprovision für neue Anbieter.
 *
 * Nicht 0: Die Anzahlung IST die Provision. Bei 0 % käme ein Betrag von
 * 0,00 € heraus, den Stripe gar nicht annimmt — die Zusage des Vermieters
 * liefe mitten im Ablauf auf "Für diese Anfrage lässt sich keine Anzahlung
 * erzeugen".
 *
 * Der Wert steht auch als Spaltenvorgabe in der Datenbank
 * (0012_provision_vorgabe.sql), damit ein Datensatz, der an diesem Modell
 * vorbei entsteht, ebenfalls nicht bei 0 landet.
 */
export const STANDARD_PROVISION = 13;

/**
 * URL-Slug für ein Angebot.
 *
 * Statt "offer_000m" steht in der Adresszeile der Anbietername — lesbar für
 * Kunden und auffindbar für Suchen wie "Event-Wunsch Fotobox mieten". Hat ein
 * Anbieter mehrere Angebote, kommt der Box-Name dazu.
 *
 * Slugs werden einmal vergeben und gespeichert: Ändert ein Anbieter später
 * seinen Namen, bleibt die URL bestehen — sonst wären alle Links tot.
 */
export function slugify(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

export function buildOfferSlug({ partnerName, boothName, withBooth }) {
  const base = slugify(partnerName);
  if (!withBooth) return base || "fotobox";
  const booth = slugify(boothName);
  // Wiederholt der Box-Name nur den Anbieter, bringt er nichts.
  if (!booth || base.includes(booth) || booth.includes(base)) return base;
  return `${base}-${booth}`;
}

/** Sorgt für Eindeutigkeit: bei Kollision wird durchnummeriert. */
export function uniqueSlug(wunsch, vergeben) {
  const basis = wunsch || "fotobox";
  let slug = basis;
  let n = 2;
  while (vergeben.has(slug)) slug = `${basis}-${n++}`;
  vergeben.add(slug);
  return slug;
}

export function newId(prefix) {
  return `${prefix}_${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

export function emptyPartner(name = "Neuer Anbieter") {
  return {
    id: newId("partner"),
    settings: {
      partnerName: name,
      firstName: "",
      email: "",
      websiteUrl: "",
      logo: "",
    },
    legal: {
      commissionPercent: STANDARD_PROVISION,
      agbUrl: "",
      privacyUrl: "",
      // Einmal zugestimmt, bleibt zugestimmt — mit Zeitstempel als Nachweis.
      partnerTermsAccepted: false,
      partnerTermsAcceptedAt: null,
    },
    // Für die spätere Rechnungsstellung über die Vermittlungsprovision.
    billing: {
      company: "",
      street: "",
      zip: "",
      city: "",
      country: "Deutschland",
      vatId: "",
      // Wird beim ersten Rechnungslauf gesetzt und danach nur noch gelesen —
      // nichts, was der Vermieter im Backend sieht oder pflegt.
      stripeCustomerId: "",
    },
    delivery: {
      selfDelivery: {
        enabled: false,
        zip: "",
        city: "",
        daysBeforeEvent: 0,
        ranges: [],
      },
      pickup: { enabled: false, zip: "", city: "" },
      post: { enabled: false, costs: 0, daysBeforeStart: 0, daysBeforeEnd: 0 },
    },
    cancellation: {
      cancellable: false,
      // Erstattungsstaffel: je früher storniert wird, desto mehr bekommt der
      // Kunde zurück. Die Staffel muss den gesamten Zeitraum abdecken, in
      // Bubble also bis 999 Tage.
      refundRanges: [],
      freeReschedule: false,
      freeRescheduleDays: 0,
    },
    printLayouts: {
      offersLayouts: false,
      layoutCount: 0,
      ownLayoutPossible: false,
    },
    photobooths: [],
    printers: [],
    extras: [],
    packages: [],
    offers: [],
  };
}

export function emptyPhotobooth() {
  return {
    id: newId("booth"),
    type: "Standard",
    name: "Neue Fotobox",
    price: 0,
    image: "",
    camType: "",
    lightType: "",
    controls: { liveView: false, remote: false, touch: false },
    screenSize: 0,
    digital: { gallery: false, usb: false, phone: false, ai: false },
  };
}

export function emptyPrinter() {
  return {
    id: newId("printer"),
    name: "Neuer Drucker",
    capacity: 0,
    speedSeconds: 0,
    price: null,
  };
}

/**
 * Ein neues Extra.
 *
 * Vorbelegt mit "Fotodruck", weil das mit Abstand die häufigste Kategorie ist
 * (97 von 253) — wer etwas anderes anlegt, stellt es in einem Griff um.
 *
 * Der Name bleibt leer, nicht "Neues Extra": Ein vorbelegter Name will
 * überschrieben werden, und wer ihn übersieht, hat "Neues Extra" im Vergleich
 * stehen. Ein leeres Feld sagt dagegen von selbst, dass da etwas hingehört.
 */
/**
 * Kategorien, deren Name sich aus den Angaben ergibt.
 *
 * „200 Ausdrucke", „20 Requisiten", „4 Stunden" — etwas anderes schreibt
 * dort niemand hin. Ein frei getippter Name führte nur dazu, dass im
 * Vergleich dieselbe Angabe zweimal stand: einmal als Name, einmal als
 * Auszeichnung daneben.
 *
 * Hintergrund und Sonstiges bleiben frei benannt: „Hintergrund mit Stativ"
 * oder „Auf- und Abbau durch uns" trägt Information, die sonst verloren ginge.
 */
export const ABGELEITETE_NAMEN = new Set([
  "Fotodruck",
  "Requisiten",
  "Stundenpaket",
]);

export function abgeleiteterName(extra = {}) {
  const { category, prints, isPrintFlatrate, itemCount } = extra;

  if (category === "Fotodruck") {
    if (isPrintFlatrate || (prints ?? 0) >= UNLIMITED_PRINTS) return "Flatrate";
    return prints ? `${prints} Ausdrucke` : "";
  }
  if (category === "Requisiten") {
    return itemCount ? `${itemCount} Requisiten` : "Requisiten";
  }
  if (category === "Stundenpaket") {
    return itemCount
      ? `${itemCount} ${itemCount === 1 ? "Stunde" : "Stunden"}`
      : "Stundenpaket";
  }
  return extra.name || "";
}

export function emptyExtra(category = "Fotodruck") {
  return {
    id: newId("extra"),
    category,
    name: "",
    description: "",
    // Leer, nicht 999. Der alte Vorgabewert bedeutete „unbegrenzt", stand
    // aber neben isPrintFlatrate: false — ein neuer Fotodruck war damit
    // „feste Anzahl: 999". Leer zwingt zur Angabe, und ohne sie lässt sich
    // das Extra nicht speichern.
    prints: null,
    isPrintFlatrate: false,
    maxPerBooking: 1,
    price: null,
    image: "",
  };
}

export function emptyPackage() {
  return {
    id: newId("package"),
    name: "Neues Paket",
    internalName: "",
    price: 0,
    printerId: null,
    extraIds: [],
    optionalExtraIds: [],
    sortOrder: 0,
    sponsored: false,
    parameter: "",
  };
}

/**
 * Freigabe-Status eines Angebots.
 *
 * Ein Anbieter kann sein Angebot nicht selbst live schalten — der erste
 * Livegang wird vom Admin geprüft. Danach darf der Anbieter es eigenständig
 * pausieren und wieder aktivieren, ohne erneute Freigabe.
 *
 *   draft      → in Arbeit, noch nie eingereicht
 *   submitted  → zur Prüfung eingereicht, wartet auf den Admin
 *   approved   → freigegeben; ab jetzt entscheidet `online` über die Sichtbarkeit
 *   rejected   → abgelehnt, mit Begründung; kann überarbeitet neu eingereicht werden
 */
export const APPROVAL = {
  DRAFT: "draft",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  REJECTED: "rejected",
};

/** Sichtbar im Vergleich ist nur, was freigegeben UND aktiv ist. */
export function isOfferVisible(offer) {
  return offer.approval === APPROVAL.APPROVED && Boolean(offer.online);
}

export function emptyOffer() {
  return {
    id: newId("offer"),
    name: "Neues Angebot",
    photoboothId: null,
    packageIds: [],
    online: false,
    approval: APPROVAL.DRAFT,
    submittedAt: null,
    approvedAt: null,
    rejectionReason: "",
    url: "",
    sortOrder: 0,
  };
}

/**
 * Rechnet die relationalen Partner-Daten in das flache Format um, das
 * data.json und damit das gesamte Frontend erwartet. Ein Eintrag entsteht pro
 * Angebot (Offer) — genau so, wie Bubble es aus NEW_Offer erzeugt hat.
 */
export function buildPhotoboothEntry(partner, offer) {
  const booth = partner.photobooths.find((b) => b.id === offer.photoboothId);
  if (!booth) return null;

  const { selfDelivery, pickup, post } = partner.delivery;

  const packages = offer.packageIds
    .map((id) => partner.packages.find((p) => p.id === id))
    .filter(Boolean)
    .map((pkg) => {
      const printer = partner.printers.find((p) => p.id === pkg.printerId);
      const extras = pkg.extraIds
        .map((id) => partner.extras.find((e) => e.id === id))
        .filter(Boolean);

      const printExtra = extras.find((e) => e.category === "Fotodruck");

      return {
        SortOrder: pkg.sortOrder || 0,
        Sponsored: Boolean(pkg.sponsored),
        PackageName: pkg.name,
        PackagePrice: pkg.price || 0,
        Print: Boolean(printExtra),
        SpecialPrintFlatrate: Boolean(printExtra?.isPrintFlatrate),
        Props: extras.some((e) => e.category === "Requisiten"),
        Background: extras.some((e) => e.category === "Hintergrund"),
        Printer: printer
          ? {
              Name: printer.name,
              PaperStorageSize: printer.capacity || 0,
              PrintTimeSeconds: printer.speedSeconds || 0,
            }
          : {},
        Parameter: pkg.parameter || "",
      };
    });

  return {
    Visibility: isOfferVisible(offer) ? "yes" : "no",
    PhotoboothName: booth.name,
    PhotoboothType: booth.type,
    PhotoboothImage: booth.image || "",
    PhotoboothPrice: booth.price || 0,
    CamType: booth.camType || "",
    LightType: booth.lightType || "",
    ControlLiveView: Boolean(booth.controls?.liveView),
    ControlRemote: Boolean(booth.controls?.remote),
    ControlTouch: Boolean(booth.controls?.touch),
    DigitalHandy: Boolean(booth.digital?.phone),
    DigitalOnlineGalerie: Boolean(booth.digital?.gallery),
    DigitalUSB: Boolean(booth.digital?.usb),
    ScreenSize: booth.screenSize || 0,
    Packages: packages,
    PartnerName: partner.settings.partnerName,
    PrintLayoutsYesNo: Boolean(partner.printLayouts?.offersLayouts),
    OwnPrintLayoutYesNo: Boolean(partner.printLayouts?.ownLayoutPossible),
    PrintLayoutsNumber: partner.printLayouts?.layoutCount || 0,
    CustomerPickUp: Boolean(pickup?.enabled),
    CustomerPickUpZip: pickup?.enabled ? pickup.zip || "" : "",
    CustomerPickUpCity: pickup?.enabled ? pickup.city || "" : "",
    SelfDelivery: Boolean(selfDelivery?.enabled),
    SelfDeliveryZip: selfDelivery?.enabled ? selfDelivery.zip || "" : "",
    SelfDeliveryCity: selfDelivery?.enabled ? selfDelivery.city || "" : "",
    SelfDeliveryDaysBeforeEvent: selfDelivery?.enabled
      ? selfDelivery.daysBeforeEvent || 0
      : 0,
    ShippingRanges: selfDelivery?.enabled
      ? (selfDelivery.ranges || []).map((r) => ({
          Start: r.start || 0,
          End: r.end || 0,
          Price: r.pricePerKm || 0,
        }))
      : [],
    FreeSchedule: Boolean(partner.cancellation?.freeReschedule),
    FreeScheduleDays: partner.cancellation?.freeRescheduleDays || 0,
    PostDelivery: Boolean(post?.enabled),
    PostDeliveryCosts: post?.enabled ? post.costs || 0 : 0,
    PostDeliveryShippingEnd: post?.enabled ? post.daysBeforeEnd || 0 : 0,
    PostDeliveryShippingStart: post?.enabled ? post.daysBeforeStart || 0 : 0,
    Intern: Boolean(partner.intern),
    Locale: "DE",
    GuruTestReport: Boolean(offer.guruTestReport),
    GuruScore: offer.guruScore || 0,
    // Die Reihenfolge im Vergleich. Hing bis zum 2026-11-02 am Paket und
    // wurde dort als Höchstwert aller Pakete gelesen — ein Paket sagt über
    // die Position der Karte aber nichts.
    SortOrder: offer.sortOrder || 0,
    URL: angebotsLink(offer),
    // Für den Clickout über /go/<slug>, siehe lib/klickAusgang.
    Slug: offer.slug || offer.id,
    ...(offer.extraFields || {}),
  };
}

/**
 * Wohin der Knopf im Vergleich führt.
 *
 * Bisher stand hier der alte Bubble-Link — 52 der 59 Angebote gingen also aus
 * unserem Vergleich heraus in ein System, das wir ablösen. Die zeigen jetzt auf
 * den eigenen Checkout.
 *
 * Die übrigen 7 sind Affiliate-Angebote (KRUU, foboxy, myselfie und andere).
 * Die sind bei uns nicht buchbar, dort verdienen wir über den Partnerlink —
 * deren Ziel bleibt unangetastet.
 */
export function angebotsLink(offer) {
  const ziel = (offer.url || "").trim();

  // Ein hinterlegtes externes Ziel gewinnt — das sind die Affiliate-Angebote
  // (KRUU, foboxy, myselfie …), die bei uns nicht buchbar sind. Alles andere
  // führt in unseren eigenen Checkout.
  //
  // Der Bubble-Test bleibt als Netz: Die 52 Alt-Einträge sind zwar gelöscht,
  // aber ein Import oder eine versehentliche Eingabe würde Kunden sonst in ein
  // abgeschaltetes System schicken.
  const external = ziel && !ziel.includes("partner.fotoboxguru.de");

  if (external) return ziel;
  return offer.slug ? `/fotobox-mieten/${offer.slug}` : "";
}

/**
 * Erzeugt die komplette Photobooths-Liste. Angebote ohne gültige Fotobox
 * fallen still raus — sonst stünden kaputte Einträge im Frontend.
 *
 * Standardmäßig nur freigegebene und aktive Angebote. Ohne diesen Filter
 * landeten auch Entwürfe, abgelehnte und pausierte Angebote im Vergleich, auf
 * der Startseite und in /api/data — und der Kunde klickte auf eine Karte, die
 * ihn auf „nicht verfügbar" führt, weil loadOfferView richtigerweise sperrt.
 * Bis zur Selbstverwaltung durch die Vermieter fiel das nicht auf: Es gab
 * schlicht kein einziges Angebot, das nicht freigegeben war.
 *
 * `nurSichtbare: false` ist für eine Admin-Vorschau gedacht — nichts, was je
 * an einen Kunden geht.
 */
export function buildDataJson(partners, { nurSichtbare = true, klickwerte } = {}) {
  const photobooths = [];
  for (const partner of partners) {
    for (const offer of partner.offers || []) {
      if (nurSichtbare && !isOfferVisible(offer)) continue;
      const entry = buildPhotoboothEntry(partner, offer);
      if (!entry) continue;
      // Was ein Klick zu diesem Partner einbringt (Teil B3). Steht am
      // Eintrag, weil der Knopf ihn beim Klick melden muss — und der kennt
      // nur das, was im Katalog steht.
      if (klickwerte) entry.KlickWert = klickwerte.get(partner.id) ?? null;
      photobooths.push(entry);
    }
  }
  return { Photobooths: photobooths };
}
